import { useCallback, useEffect, useRef, useState } from "react";
import type { SectionProps } from "./context";
import { Band, Head, cn, rv } from "./ui";
import { api, apiErrorText } from "@/lib/api";
import { useAuth, type Role } from "./auth";
import { goAnchor } from "./app-nav";
import { showContact } from "./contacts-api";
import { showCompany } from "./companies-api";
import { showDeal } from "./deals-api";
import { notesApi, takeNewTask, tasksApi, type Note, type Parent, type Task, type TaskInput, type TaskPriority } from "./activity-api";
import { locale, t, tn } from "@/lib/i18n";

/* Заметки и задачи (этап 5, часть 2): блоки в карточке контакта и сделки + раздел «Задачи» (мои и всей команды).
   Из Zoho CRM переносятся заметки и задачи контактов, сделок, компаний и лидов (лид → контакт по телефону/почте). */

const input =
  "h-9 w-full rounded-btn border border-border bg-background px-2.5 text-[12.5px] text-foreground outline-none transition focus:border-primary";
const btn = "inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border bg-background px-2.5 text-[12px] font-semibold text-foreground transition hover:bg-muted disabled:cursor-default disabled:opacity-50";
const primary = "inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-2.5 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-default disabled:opacity-50";

const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString(locale, { dateStyle: "short", timeStyle: "short" }) : "—");
const day = (iso: string | null | undefined) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(locale, { day: "numeric", month: "short" }) : "");
const today = () => new Date().toISOString().slice(0, 10);
const PRIORITY_LABEL: Record<TaskPriority, string> = { low: t("низкий"), normal: t("обычный"), high: t("высокий") };

/* ---------------------------------------------------------------- заметки в карточке */

export function NotesBlock({ parent, onFlash }: { parent: Parent; onFlash?: (text: string) => void }) {
  const { user } = useAuth();
  const canWrite = user?.role !== "viewer";
  const canEdit = (n: Note) => n.authorId === user?.id || user?.role === "owner" || user?.role === "admin";
  const [notes, setNotes] = useState<Note[]>([]);
  const [text, setText] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  const key = `${parent.contactId ?? ""}|${parent.dealId ?? ""}|${parent.companyId ?? ""}`;

  const load = useCallback(() => notesApi.list(parent).then(setNotes, () => undefined), [key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    void load();
  }, [load]);

  const add = async () => {
    const body = text.trim();
    if (!body) return;
    try {
      await notesApi.create(parent, body);
      setText("");
      await load();
    } catch (e) {
      onFlash?.(apiErrorText(e));
    }
  };
  const save = async (id: string, body: string) => {
    try {
      await notesApi.update(id, body);
      setEditing(null);
      await load();
    } catch (e) {
      onFlash?.(apiErrorText(e));
    }
  };
  const remove = async (n: Note) => {
    if (!window.confirm(t("Удалить заметку?"))) return;
    try {
      await notesApi.remove(n.id);
      await load();
    } catch (e) {
      onFlash?.(apiErrorText(e));
    }
  };
  const shown = all ? notes : notes.slice(0, 5);

  return (
    <div className="rounded-btn border border-border bg-background/60 px-3 py-2.5">
      <span className="text-[11.5px] font-semibold">
        {t("Заметки")}
        {notes.length ? ` · ${notes.length}` : ""}
      </span>
      {canWrite ? (
        <div className="mt-1.5 flex items-end gap-1.5">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") void add();
            }}
            rows={text.includes("\n") || text.length > 60 ? 3 : 1}
            placeholder={t("Новая заметка… (⌘↵ — сохранить)")}
            aria-label={t("Новая заметка")}
            className={cn(input, "h-auto min-h-9 resize-none py-2")}
          />
          <button type="button" disabled={!text.trim()} onClick={() => void add()} className={primary}>
            {t("Добавить")}
          </button>
        </div>
      ) : null}
      <ul className="mt-1.5 flex flex-col divide-y divide-border/60">
        {shown.map((n) => (
          <li key={n.id} className="py-1.5 text-[12px]">
            <div className="flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground">
              <span className="font-semibold text-foreground">{n.author || "—"}</span>
              <span className="tabular-nums">{when(n.createdAt)}</span>
              {n.fromCrm ? <span>· Zoho CRM</span> : null}
              {canWrite && canEdit(n) && editing !== n.id ? (
                <span className="ms-auto flex gap-2">
                  <button type="button" onClick={() => setEditing(n.id)} className="cursor-pointer hover:text-foreground">
                    {t("изменить")}
                  </button>
                  <button type="button" onClick={() => void remove(n)} className="cursor-pointer hover:text-red-600">
                    {t("удалить")}
                  </button>
                </span>
              ) : null}
            </div>
            {editing === n.id ? (
              <NoteEditor initial={n.body} onCancel={() => setEditing(null)} onSave={(b) => void save(n.id, b)} />
            ) : (
              <>
                {n.title ? <p className="mt-0.5 font-semibold">{n.title}</p> : null}
                <p className="mt-0.5 whitespace-pre-wrap break-words leading-relaxed">{n.body}</p>
              </>
            )}
          </li>
        ))}
      </ul>
      {notes.length > 5 && !all ? (
        <button type="button" onClick={() => setAll(true)} className="mt-1 cursor-pointer text-[11.5px] font-semibold text-primary hover:underline">
          {t("Показать все ({n})", { n: notes.length })}
        </button>
      ) : null}
      {notes.length === 0 ? <p className="mt-1 text-[11.5px] text-muted-foreground">{t("Заметок пока нет.")}</p> : null}
    </div>
  );
}

function NoteEditor({ initial, onCancel, onSave }: { initial: string; onCancel: () => void; onSave: (body: string) => void }) {
  const [v, setV] = useState(initial);
  return (
    <div className="mt-1">
      <textarea value={v} onChange={(e) => setV(e.target.value)} rows={4} aria-label={t("Текст заметки")} className={cn(input, "h-auto resize-y py-2")} />
      <div className="mt-1 flex gap-1.5">
        <button type="button" disabled={!v.trim()} onClick={() => onSave(v.trim())} className={primary}>
          {t("Сохранить")}
        </button>
        <button type="button" onClick={onCancel} className={btn}>
          {t("Отмена")}
        </button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- задачи в карточке */

export function TasksBlock({ parent, onFlash }: { parent: Parent; onFlash?: (text: string) => void }) {
  const { user } = useAuth();
  const canWrite = user?.role !== "viewer";
  const [tasks, setTasks] = useState<Task[]>([]);
  const [showDone, setShowDone] = useState(false);
  const [adding, setAdding] = useState(false);
  const key = `${parent.contactId ?? ""}|${parent.dealId ?? ""}|${parent.companyId ?? ""}`;

  const load = useCallback(
    () =>
      tasksApi
        .list({ contact: parent.contactId ?? "", deal: parent.dealId ?? "", company: parent.companyId ?? "", status: showDone ? "" : "active", size: 50 })
        .then((r) => setTasks(r.items), () => undefined),
    [key, showDone], // eslint-disable-line react-hooks/exhaustive-deps
  );
  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (x: Task) => {
    try {
      await tasksApi.update(x.id, { status: x.status === "done" ? "open" : "done" });
      await load();
    } catch (e) {
      onFlash?.(apiErrorText(e));
    }
  };

  return (
    <div className="rounded-btn border border-border bg-background/60 px-3 py-2.5">
      <div className="flex items-center gap-2">
        <span className="flex-1 text-[11.5px] font-semibold">
          {t("Задачи")}
          {tasks.length ? ` · ${tasks.length}` : ""}
        </span>
        <button type="button" onClick={() => setShowDone((v) => !v)} className="cursor-pointer text-[11px] text-muted-foreground hover:text-foreground">
          {showDone ? t("скрыть сделанные") : t("со сделанными")}
        </button>
        {canWrite ? (
          <button type="button" onClick={() => setAdding((v) => !v)} className="cursor-pointer text-[11.5px] font-semibold text-primary hover:underline">
            + {t("Задача")}
          </button>
        ) : null}
      </div>
      {adding ? (
        <TaskForm
          draft={{ ...parent }}
          onCancel={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            void load();
          }}
          onFlash={onFlash}
        />
      ) : null}
      <ul className="mt-1 flex flex-col">
        {tasks.map((x) => (
          <TaskRow key={x.id} task={x} canWrite={canWrite} onToggle={() => void toggle(x)} compact />
        ))}
      </ul>
      {tasks.length === 0 ? <p className="mt-1 text-[11.5px] text-muted-foreground">{t("Задач нет.")}</p> : null}
    </div>
  );
}

function TaskRow({ task: x, canWrite, onToggle, onOpen, compact }: { task: Task; canWrite: boolean; onToggle: () => void; onOpen?: () => void; compact?: boolean }) {
  const done = x.status === "done";
  return (
    <li className={cn("flex items-start gap-2 py-1.5", !compact && "px-3")}>
      <input type="checkbox" checked={done} disabled={!canWrite} onChange={onToggle} aria-label={t("Сделано")} className="mt-0.5 size-4 cursor-pointer accent-primary" />
      <div className="min-w-0 flex-1">
        <button type="button" onClick={onOpen} className={cn("block w-full cursor-pointer text-start text-[12.5px] font-semibold hover:underline", done && "text-muted-foreground line-through", !onOpen && "cursor-default hover:no-underline")}>
          {x.title}
        </button>
        <p className="flex flex-wrap gap-x-2 text-[11px] text-muted-foreground">
          {x.dueDate ? <span className={cn("tabular-nums", x.overdue && "font-semibold text-red-600")}>{x.overdue ? t("просрочено · {d}", { d: day(x.dueDate) }) : day(x.dueDate)}</span> : null}
          {x.priority === "high" ? <span className="text-amber-600">{t("важно")}</span> : null}
          {x.status === "deferred" ? <span>{t("отложена")}</span> : null}
          {x.assignee ? <span>{x.assignee}</span> : null}
          {!compact && x.contact ? <Link onClick={() => { showContact(x.contact!.id); goAnchor("#contacts-app"); }} icon="ri-user-line" label={x.contact.name} /> : null}
          {!compact && x.deal ? <Link onClick={() => { showDeal(x.deal!.id); goAnchor("#deals"); }} icon="ri-briefcase-line" label={x.deal.title} /> : null}
          {!compact && x.company ? <Link onClick={() => { showCompany(x.company!.id); goAnchor("#companies"); }} icon="ri-building-2-line" label={x.company.name} /> : null}
          {x.fromCrm ? <span>Zoho CRM</span> : null}
        </p>
        {!compact && x.description ? <p className="mt-0.5 line-clamp-2 whitespace-pre-wrap text-[11.5px] text-muted-foreground">{x.description}</p> : null}
      </div>
    </li>
  );
}

function Link({ onClick, icon, label }: { onClick: () => void; icon: string; label: string }) {
  return (
    <button type="button" onClick={onClick} className="inline-flex max-w-[220px] cursor-pointer items-center gap-1 truncate text-primary hover:underline">
      <i className={icon} aria-hidden />
      <span className="truncate">{label}</span>
    </button>
  );
}

type Owner = { id: string; name: string };

function useOwners(): Owner[] {
  const { user } = useAuth();
  const [owners, setOwners] = useState<Owner[]>([]);
  useEffect(() => {
    if (!user) return;
    const me = [{ id: user.id, name: user.name || user.email }];
    const staff = user.role === "owner" || user.role === "admin" || user.role === "manager";
    if (!staff) {
      setOwners(me);
      return;
    }
    api<{ id: string; name: string; email: string; role: Role; status: string }[]>("/users")
      .then((rows) => setOwners(rows.filter((r) => r.status !== "disabled" && r.role !== "viewer").map((r) => ({ id: r.id, name: r.name || r.email }))))
      .catch(() => setOwners(me));
  }, [user]);
  return owners;
}

function TaskForm({ draft, task, onCancel, onSaved, onFlash }: { draft?: TaskInput; task?: Task; onCancel: () => void; onSaved: (x: Task) => void; onFlash?: (text: string) => void }) {
  const { user } = useAuth();
  const owners = useOwners();
  const [form, setForm] = useState<TaskInput>(() =>
    task
      ? { title: task.title, description: task.description, dueDate: task.dueDate, priority: task.priority, assigneeId: task.assigneeId, status: task.status }
      : { title: "", description: "", dueDate: null, priority: "normal", assigneeId: user?.id ?? null, ...draft },
  );
  const [saving, setSaving] = useState(false);
  const set = (patch: TaskInput) => setForm((f) => ({ ...f, ...patch }));
  const submit = async () => {
    if (!form.title?.trim()) return;
    setSaving(true);
    try {
      const saved = task ? await tasksApi.update(task.id, form) : await tasksApi.create(form);
      onSaved(saved);
    } catch (e) {
      onFlash?.(apiErrorText(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className="mt-2 flex flex-col gap-1.5 rounded-btn border border-border bg-card p-2"
    >
      <input autoFocus value={form.title ?? ""} onChange={(e) => set({ title: e.target.value })} placeholder={t("Что сделать")} aria-label={t("Название задачи")} className={input} />
      <textarea value={form.description ?? ""} onChange={(e) => set({ description: e.target.value })} rows={2} placeholder={t("Подробности (необязательно)")} aria-label={t("Подробности")} className={cn(input, "h-auto resize-y py-2")} />
      <div className="flex flex-wrap gap-1.5">
        <input type="date" value={form.dueDate ?? ""} onChange={(e) => set({ dueDate: e.target.value || null })} aria-label={t("Срок")} className={cn(input, "w-auto")} />
        <select value={form.priority ?? "normal"} onChange={(e) => set({ priority: e.target.value as TaskPriority })} aria-label={t("Приоритет")} className={cn(input, "w-auto")}>
          {(Object.keys(PRIORITY_LABEL) as TaskPriority[]).map((p) => (
            <option key={p} value={p}>
              {PRIORITY_LABEL[p]}
            </option>
          ))}
        </select>
        {owners.length > 1 ? (
          <select value={form.assigneeId ?? ""} onChange={(e) => set({ assigneeId: e.target.value || null })} aria-label={t("Ответственный")} className={cn(input, "w-auto max-w-[200px]")}>
            {owners.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        ) : null}
      </div>
      <div className="flex gap-1.5">
        <button type="submit" disabled={saving || !form.title?.trim()} className={primary}>
          {saving ? t("Сохраняю…") : task ? t("Сохранить") : t("Создать")}
        </button>
        <button type="button" onClick={onCancel} className={btn}>
          {t("Отмена")}
        </button>
      </div>
    </form>
  );
}

/* ---------------------------------------------------------------- раздел «Задачи» */

const DUE_GROUPS = [
  { id: "overdue", label: t("Просрочено") },
  { id: "today", label: t("Сегодня") },
  { id: "week", label: t("На неделе") },
  { id: "later", label: t("Позже") },
  { id: "none", label: t("Без срока") },
] as const;

function groupOf(x: Task): (typeof DUE_GROUPS)[number]["id"] {
  if (!x.dueDate) return "none";
  const td = today();
  if (x.dueDate < td) return "overdue";
  if (x.dueDate === td) return "today";
  const week = new Date();
  week.setDate(week.getDate() + 7);
  return x.dueDate <= week.toISOString().slice(0, 10) ? "week" : "later";
}

function TasksDemo({ title, subtitle }: SectionProps) {
  const { user } = useAuth();
  const canWrite = user?.role !== "viewer";
  const staff = user?.role === "owner" || user?.role === "admin" || user?.role === "manager" || user?.role === "viewer";
  const [scope, setScope] = useState<"me" | "all">(() => (user?.role === "viewer" ? "all" : "me"));
  const [status, setStatus] = useState<"active" | "done">("active");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<Task[]>([]);
  const [total, setTotal] = useState(0);
  const [mine, setMine] = useState({ overdue: 0, today: 0, open: 0 });
  const [draft, setDraft] = useState<TaskInput | null>(() => takeNewTask());
  const [editing, setEditing] = useState<Task | null>(null);
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const flash = useCallback((text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4600);
  }, []);

  const load = useCallback(
    () =>
      tasksApi.list({ scope, status, q, size: 300 }).then(
        (r) => {
          setRows(r.items);
          setTotal(r.total);
          setMine(r.mine);
        },
        (e) => flash(apiErrorText(e)),
      ),
    [scope, status, q, flash],
  );
  useEffect(() => {
    const h = setTimeout(() => void load(), 150);
    return () => clearTimeout(h);
  }, [load]);

  const toggle = async (x: Task) => {
    try {
      await tasksApi.update(x.id, { status: x.status === "done" ? "open" : "done" });
      await load();
      flash(x.status === "done" ? t("Задача снова открыта") : t("Задача сделана"));
    } catch (e) {
      flash(apiErrorText(e));
    }
  };
  const remove = async (x: Task) => {
    if (!window.confirm(t("Удалить задачу «{title}»?", { title: x.title }))) return;
    try {
      await tasksApi.remove(x.id);
      setEditing(null);
      await load();
    } catch (e) {
      flash(apiErrorText(e));
    }
  };

  const groups = DUE_GROUPS.map((g) => ({ ...g, items: rows.filter((x) => groupOf(x) === g.id) })).filter((g) => g.items.length);

  return (
    <Band id="tasks" tone="muted">
      <Head title={title} subtitle={subtitle} />
      <div {...rv(1)} className="mt-6 overflow-hidden rounded-card border border-border bg-card p-3.5 shadow-sm md:p-5">
        <div className="flex flex-wrap items-center gap-2">
          {staff && user?.role !== "viewer" ? (
            <div className="flex gap-1.5">
              {(["me", "all"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScope(s)}
                  className={cn("h-8 cursor-pointer rounded-full border px-3 text-[12px] font-semibold transition", scope === s ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground")}
                >
                  {s === "me" ? t("Мои") : t("Все")}
                </button>
              ))}
            </div>
          ) : null}
          <div className="flex gap-1.5">
            {(["active", "done"] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                className={cn("h-8 cursor-pointer rounded-full border px-3 text-[12px] font-semibold transition", status === s ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground")}
              >
                {s === "active" ? t("В работе") : t("Сделанные")}
              </button>
            ))}
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Поиск по задачам")} aria-label={t("Поиск по задачам")} className={cn(input, "max-w-xs")} />
          {canWrite ? (
            <button type="button" onClick={() => setDraft({})} className={cn(primary, "ms-auto h-9")}>
              + {t("Задача")}
            </button>
          ) : null}
        </div>
        <p className="mt-2 text-[12px] text-muted-foreground">
          {t("Показано {n} из {total}", { n: rows.length, total })}
          {mine.open ? ` · ${t("у меня в работе {n}", { n: tn(mine.open, "задача", "задачи", "задач") })}` : ""}
          {mine.overdue ? ` · ${t("просрочено {n}", { n: mine.overdue })}` : ""}
          {mine.today ? ` · ${t("на сегодня {n}", { n: mine.today })}` : ""}
        </p>

        {draft ? <TaskForm draft={draft} onCancel={() => setDraft(null)} onSaved={() => { setDraft(null); void load(); flash(t("Задача создана")); }} onFlash={flash} /> : null}
        {editing ? (
          <div className="mt-2">
            <TaskForm task={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); void load(); flash(t("Задача сохранена")); }} onFlash={flash} />
            <button type="button" onClick={() => void remove(editing)} className={cn(btn, "mt-1.5 text-red-600")}>
              {t("Удалить задачу")}
            </button>
          </div>
        ) : null}

        {status === "active" ? (
          groups.map((g) => (
            <section key={g.id} className="mt-4" aria-label={g.label}>
              <h4 className={cn("text-[12px] font-semibold uppercase tracking-wide", g.id === "overdue" ? "text-red-600" : "text-muted-foreground")}>
                {g.label} · {g.items.length}
              </h4>
              <ul className="mt-1 flex flex-col divide-y divide-border/60 rounded-btn border border-border">
                {g.items.map((x) => (
                  <TaskRow key={x.id} task={x} canWrite={canWrite} onToggle={() => void toggle(x)} onOpen={canWrite ? () => setEditing(x) : undefined} />
                ))}
              </ul>
            </section>
          ))
        ) : (
          <ul className="mt-4 flex flex-col divide-y divide-border/60 rounded-btn border border-border">
            {rows.map((x) => (
              <TaskRow key={x.id} task={x} canWrite={canWrite} onToggle={() => void toggle(x)} onOpen={canWrite ? () => setEditing(x) : undefined} />
            ))}
          </ul>
        )}
        {rows.length === 0 ? <p className="mt-4 rounded-btn border border-border px-3 py-6 text-center text-[12.5px] text-muted-foreground">{t("Задач нет — хорошая новость.")}</p> : null}
      </div>
      {toast ? (
        <div role="status" className="fixed bottom-5 start-1/2 z-50 -translate-x-1/2 rounded-btn bg-foreground px-4 py-2.5 text-[13px] text-background shadow-lg">
          {toast}
        </div>
      ) : null}
    </Band>
  );
}

export default TasksDemo;
